import React, { ReactNode, useCallback } from 'react';
import { MenuOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { Button, Dropdown } from 'antd';
import type { MenuProps as AntdMenuProps } from 'antd';
import type { RemapObjectType } from '@owl-lib/type-util';
import type { AriaLabelProps, CoreHTMLProps } from '../../shared/interface';
import Icon from '../Icon/Icon';
import styles from './Menu.module.scss';

export type MenuItemPropsBase<MenuKey extends string = string> = {
  key: MenuKey;
  label?: string | ReactNode;
  icon?: ReactNode;
  href?: string;
  disabled?: boolean;
  divider?: boolean;
  'data-testid'?: string;
};

export type MenuItemProps<MenuKey extends string = string> =
  MenuItemPropsBase<MenuKey> &
    ({ label: string | ReactNode } | { divider: true });

type MenuItemClickEvent = React.MouseEvent<
  HTMLButtonElement | HTMLAnchorElement,
  MouseEvent
>;

export interface MenuProps<MenuKey extends string = string>
  extends CoreHTMLProps,
    AriaLabelProps {
  menuItems?: MenuItemProps<MenuKey>[];
  content?: React.ReactNode;
  onClick?: (key: MenuKey, event: MenuItemClickEvent) => void;
  menuClassName?: string;
  buttonClassName?: string;
  placement?:
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight';
  disabled?: boolean;
  focusable?: boolean;
  inPortal?: boolean;
}

interface MenuComponent extends RemapObjectType<React.FC<MenuProps>> {
  <MenuKey extends string = string>(
    props: React.PropsWithChildren<MenuProps<MenuKey>>
  ): React.ReactElement<any, any>;
}

const Menu: MenuComponent = (props) => {
  const {
    menuItems,
    onClick,
    children = (
      <Icon>
        <MenuOutlined />
      </Icon>
    ),
    buttonClassName,
    menuClassName,
    content,
    placement,
    'aria-label': ariaLabel = 'menu',
    disabled = !menuItems,
    focusable,
    ...rest
  } = props;

  const onMenuItemClick = useCallback(
    (key, event) => {
      onClick?.(key, event);
    },
    [onClick]
  );

  const onMenuClick = useCallback<NonNullable<AntdMenuProps['onClick']>>(
    ({ key, domEvent }) => {
      domEvent.stopPropagation();
      domEvent.preventDefault();
      onMenuItemClick(key, domEvent);
    },
    [onMenuItemClick]
  );

  const items: AntdMenuProps['items'] = (menuItems ?? []).map(
    ({ key, ...menuItemProps }) => {
      if (menuItemProps.divider) {
        return { type: 'divider' as const, key };
      }

      const {
        icon,
        label,
        disabled: itemDisabled,
        'data-testid': dataTestId,
      } = menuItemProps;

      return {
        key,
        className: clsx(styles.menuItem, itemDisabled && styles.disabled),
        disabled: itemDisabled,
        label: (
          <span data-testid={dataTestId}>
            {icon && <Icon>{icon}</Icon>}
            <span>{label}</span>
          </span>
        ),
      };
    }
  );

  return (
    <Dropdown
      menu={{ items, className: menuClassName, onClick: onMenuClick }}
      placement={placement}
      disabled={disabled}
    >
      <Button
        {...rest}
        className={buttonClassName}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        {children}
      </Button>
    </Dropdown>
  );
};

export default Menu;
